import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';

/**
 * Custom RouteReuseStrategy that prevents route reuse when _reload query parameter is present.
 * This forces component destruction and recreation, enabling route reload functionality.
 *
 * Strategy:
 * - For child routes (vertical-nav): Store/retrieve routes to properly handle reload
 * - For root routes (alt-nav): Don't store routes to avoid infinite loops
 */
export class RouteReloadStrategy implements RouteReuseStrategy {
  private routeStore = new Map<string, DetachedRouteHandle>();
  private reloadAttachEligibleKeys = new Set<string>();

  /**
   * Detach routes for storage ONLY if they are child routes (not root routes).
   * This allows vertical-nav reloads to work while preventing alt-nav infinite loops.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const hasReload = !!route.queryParams?._reload;
    const isChildRoute = this.isChildRoute(route);
    const hasCanDeactivateGuard = this.hasCanDeactivateGuard(route);

    // Only detach child routes (vertical-nav scenario)
    // Don't detach routes with _reload (they're being reloaded)
    return isChildRoute && !hasReload && !hasCanDeactivateGuard;
  }

  /**
   * Store routes using their path as the key, excluding _reload parameter.
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.getRouteKey(route);
    if (handle) {
      this.routeStore.set(key, handle);
    }
  }

  /**
   * Attach stored routes ONLY for child routes.
   * Never attach if the route has _reload parameter.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const hasReload = !!route.queryParams?._reload;
    const key = this.getRouteKey(route);
    const hasStored = this.routeStore.has(key);
    const isChildRoute = this.isChildRoute(route);
    const hasCanDeactivateGuard = this.hasCanDeactivateGuard(route);
    const isReloadEligible = this.reloadAttachEligibleKeys.has(key);

    // Never attach routes with _reload (they need fresh creation)
    // Only attach child routes (vertical-nav scenario)
    const shouldAttach =
      !hasReload && isChildRoute && hasStored && !hasCanDeactivateGuard && isReloadEligible;

    if (shouldAttach) {
      // Once we reattach, remove eligibility flag
      this.reloadAttachEligibleKeys.delete(key);
      return true;
    }

    // If route was stored but not eligible for reload, delete it to avoid stale caches
    if (!hasReload && hasStored && !hasCanDeactivateGuard && !isReloadEligible) {
      this.routeStore.delete(key);
    }

    return false;
  }

  /**
   * Retrieve stored route if available.
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    const handle = this.routeStore.get(key) || null;

    return handle;
  }

  /**
   * Check if a route is a child route (has parent segments).
   * Root routes like /brand, /store are NOT child routes.
   * Child routes like /store/:id/general ARE child routes.
   */
  private isChildRoute(route: ActivatedRouteSnapshot): boolean {
    // Count the depth of the route
    let depth = 0;
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      if (current.url.length > 0) {
        depth += current.url.length;
      }
      current = current.parent;
    }

    // Child routes have depth > 1 (e.g., /store/:id/general = 3 segments)
    // Root routes have depth <= 1 (e.g., /store = 1 segment)
    return depth > 1;
  }

  /**
   * Generate a unique key for a route, excluding _reload parameter.
   */
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    const configPath = this.getRouteConfigPath(route);
    if (configPath) {
      return configPath;
    }

    return this.getConcreteRoutePath(route);
  }

  /**
   * Builds a normalized path using the configured route definitions (routeConfig.path)
   * so that parameterized routes (e.g., :id) map to the same cache entry
   * regardless of the specific parameter value. This keeps components such as
   * vertical-nav alive (so tab selection persists) when navigating between
   * entities that share the same route definition. Falls back to concrete segments
   * when no route configuration is present (e.g., dynamically created routes).
   */
  private getRouteConfigPath(route: ActivatedRouteSnapshot): string | null {
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;
    let hasConfigPath = false;

    while (current) {
      const configPath = current.routeConfig?.path;
      if (configPath && configPath.length > 0) {
        hasConfigPath = true;
        segments.unshift(configPath);
      } else if (current.url.length > 0) {
        segments.unshift(...current.url.map((s) => s.toString()));
      }
      current = current.parent;
    }

    if (!hasConfigPath || segments.length === 0) {
      return null;
    }

    return '/' + segments.join('/');
  }

  /**
   * Get the full path of a route from its concrete URL segments.
   */
  private getConcreteRoutePath(route: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      if (current.url.length > 0) {
        segments.unshift(...current.url.map((s) => s.toString()));
      }
      current = current.parent;
    }

    return '/' + segments.join('/');
  }

  /**
   * Determines if the route has an explicit CanDeactivate guard configured.
   * Routes with guards should not be cached so that their state is reset when navigating away.
   */
  private hasCanDeactivateGuard(route: ActivatedRouteSnapshot): boolean {
    const canDeactivate = route.routeConfig?.canDeactivate;
    return Array.isArray(canDeactivate) && canDeactivate.length > 0;
  }

  /**
   * Determines if a route should be reused.
   * Returns false when _reload query parameter is present to force reload.
   * Also checks if we're in cleanup phase (removing _reload) to prevent infinite loops.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // Check if routes have same config
    if (future.routeConfig !== curr.routeConfig) {
      return false;
    }

    const samePath = this.getRouteKey(future) === this.getRouteKey(curr);

    // Check _reload parameter state
    const futureHasReload = !!future.queryParams?._reload;
    const currHasReload = !!curr.queryParams?._reload;

    // Case 1: Adding _reload (normal → reload) → DON'T reuse
    if (futureHasReload && !currHasReload) {
      return false;
    }

    // Case 2: Removing _reload (reload → normal) → REUSE (cleanup, prevents infinite loop)
    if (!futureHasReload && currHasReload) {
      if (samePath) {
        const key = this.getRouteKey(future);
        this.reloadAttachEligibleKeys.add(key);
        return true;
      }
      return false;
    }

    // Case 3: Both have _reload (reload → reload with different timestamp) → DON'T reuse
    if (futureHasReload && currHasReload) {
      return false;
    }

    // Case 4: Neither has _reload → REUSE (normal navigation on same route)
    return samePath;
  }
}
